'use client';

import { WindowAdapter } from 'builder/WindowAdapter';
import type { SearchResult } from 'conversational-search-client';
import React, { type JSX, useMemo, useState } from 'react';

import { Skeleton, cn, getAccessibleFocusStyles } from '@videoblocks/design-system';
import { LoggerFactory } from '@videoblocks/typescript-logger';

import {
    type DownloadResult,
    downloadStockItemAction,
    downloadStockItemPreviewAction,
} from '../../../_actions/downloadActions';
import { DEFAULT_DOWNLOAD_ERROR_MESSAGE, useChatSessionContext } from '../../../_context/SessionContext';
import { DownloadAction, ResultCardHoverOverlay } from '../ResultCardOverlay';
import { type ResultEvent, trackResultEvent } from '../trackResultEvent';
import { ImageDownloadPopover } from './ImageDownloadPopover';
import { buildImageFormats } from './buildImageFormats';

const logger = LoggerFactory.create('convSearchImageResultCard');

// Vector thumbnails are line art on transparency; covering would crop the artwork.
const VECTOR_CONTENT_TYPE = 'vector';

// TODO: conv-search-api builds every detailsUrl as `/${contentClass}/stock/…`, but the image details
// route is plural; drop this once SearchResultEnrichmentService emits `/images/stock/…`.
function toImageDetailsHref(detailsUrl: string): string {
    return detailsUrl.replace(/^\/image\/stock\//, '/images/stock/');
}

interface ImageResultCardProps {
    result: SearchResult;
    aspectRatio: number;
    isTopResult?: boolean;
    onAspectRatioMeasured?: (contentId: number, aspectRatio: number) => void;
}

export function ImageResultCard({
    result,
    aspectRatio,
    isTopResult = false,
    onAspectRatioMeasured,
}: ImageResultCardProps): JSX.Element {
    const { canDownloadImage, setDownloadError } = useChatSessionContext();
    const [isLoaded, setIsLoaded] = useState(false);
    const [showPopover, setShowPopover] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const formats = useMemo(() => buildImageFormats(result.downloadEncodings ?? []), [result.downloadEncodings]);
    const isVector = result.contentType === VECTOR_CONTENT_TYPE;

    async function runDownload(request: () => Promise<DownloadResult>, successEvent: ResultEvent): Promise<void> {
        setIsDownloading(true);
        try {
            const downloadResult = await request();
            if (downloadResult.ok) {
                trackResultEvent(successEvent, result.contentId, isTopResult);
                WindowAdapter.setLocation(downloadResult.url);
            } else {
                setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
            }
        } catch (error: unknown) {
            logger.warn(
                { error, stockblocksId: result.stockblocksId, contentClass: result.contentClass },
                'Image download action threw on client',
            );
            setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
        } finally {
            setIsDownloading(false);
        }
    }

    async function downloadFormat(formatName: string): Promise<void> {
        await runDownload(
            () => downloadStockItemAction(result.stockblocksId, formatName, result.contentClass),
            'item.download',
        );
        closePopover();
    }

    function closePopover(): void {
        setShowPopover(false);
    }

    function handleDownloadClick(e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();

        if (!canDownloadImage) {
            void runDownload(
                () => downloadStockItemPreviewAction(result.stockblocksId, result.contentClass),
                'preview.downloaded',
            );
            return;
        }
        // A single format needs no choice, matching the image search page.
        if (formats.length === 1) {
            void downloadFormat(formats[0].formatName);
            return;
        }
        setShowPopover(true);
    }

    function handleImageLoad(e: React.SyntheticEvent<HTMLImageElement>): void {
        setIsLoaded(true);
        const { naturalWidth, naturalHeight } = e.currentTarget;
        if (naturalWidth > 0 && naturalHeight > 0) {
            onAspectRatioMeasured?.(result.contentId, naturalWidth / naturalHeight);
        }
    }

    return (
        <div
            className={cn(
                'relative size-full rounded-md',
                isVector ? 'bg-white' : 'bg-gray-100',
                !showPopover && 'group',
            )}
        >
            {!isLoaded && <Skeleton aria-hidden className="absolute inset-0 size-full rounded-md" />}
            <a
                href={toImageDetailsHref(result.detailsUrl)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={result.title}
                className={cn('absolute inset-0 rounded-md', getAccessibleFocusStyles())}
                onClick={() => trackResultEvent('item.clickthrough', result.contentId, isTopResult)}
            >
                {/* Thumbnails come from the content CDN at their native size; next/image would re-encode them. */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                    src={result.thumbnailUrl}
                    alt=""
                    loading="lazy"
                    onLoad={handleImageLoad}
                    onError={() => setIsLoaded(true)}
                    className={cn('size-full rounded-md', isVector ? 'object-contain' : 'object-cover')}
                />
            </a>

            <ResultCardHoverOverlay title={result.title} />

            <DownloadAction
                isPortrait={aspectRatio < 1}
                label={canDownloadImage ? 'Download Image' : 'Download Preview'}
                disabled={isDownloading}
                onClick={handleDownloadClick}
            />

            {canDownloadImage && showPopover && (
                <ImageDownloadPopover
                    formats={formats}
                    isDownloading={isDownloading}
                    onSelectFormat={(formatName) => void downloadFormat(formatName)}
                    onClose={closePopover}
                />
            )}
        </div>
    );
}
